import React, { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope, UserCheck, Clock3, Wallet, FileText, Video,
  Stethoscope as Illness, XCircle, ChevronDown, ShieldCheck,
} from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import useFaqJsonLd from '../hooks/useFaqJsonLd';
import useDoctorRegistrationOpen from '../hooks/useDoctorRegistrationOpen';
import { FUNNEL, trackFunnelStep } from '../services/analytics';
import usePricing from '../hooks/usePricing';
import { describePricing, formatCOP } from '../config/pricing';
import { HEALTH_GUIDES } from '../config/content';

// Recibe la tarifa del backend; con pricing null la respuesta de precio queda neutra.
const buildQuestions = (pricing) => [
  {
    icon: UserCheck,
    q: '¿Quién me atenderá?',
    a: 'Te atiende un médico general colombiano, verificado y habilitado legalmente para ejercer, con registro y matrícula profesional vigente.',
  },
  {
    icon: Wallet,
    q: '¿Cuánto cuesta?',
    a: `${describePricing(pricing)} Sin mensualidades ni cobros ocultos: pagas solo por la consulta que agendas y ves el valor exacto antes de confirmarla.`,
  },
  {
    icon: Clock3,
    q: '¿Cuánto dura?',
    a: 'Cada consulta está pensada para resolver tu motivo de consulta con calma, sin límites rígidos de tiempo que comprometan tu atención.',
  },
  {
    icon: FileText,
    q: '¿Recibo receta?',
    a: 'Sí. Si tu caso lo requiere, el médico puede generar fórmula médica y recomendaciones digitales directamente en la plataforma.',
  },
  {
    icon: Video,
    q: '¿Es por videollamada?',
    a: 'Sí, la consulta se realiza por videollamada o chat, desde tu celular o computador, sin necesidad de instalar aplicaciones adicionales.',
  },
  {
    icon: Illness,
    q: '¿Qué enfermedades atienden?',
    a: 'Atendemos consultas de medicina general: síntomas comunes, gripa, dolor de cabeza, control de enfermedades crónicas ya diagnosticadas, dudas sobre medicamentos, certificados y orientación médica general, entre otros.',
  },
  {
    icon: XCircle,
    q: '¿Qué enfermedades NO atienden?',
    a: 'DocEnLinea NO atiende emergencias ni urgencias vitales (dolor de pecho intenso, dificultad respiratoria severa, accidentes graves, entre otros). Tampoco reemplaza procedimientos, cirugías o exámenes físicos presenciales. Ante una urgencia, llama al 123 o acude al servicio de urgencias más cercano.',
  },
];

function FaqRow({ item, isOpen, onToggle }) {
  const Icon = item.icon;
  const panelId = useId();
  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-primary-600 flex-shrink-0" />
          <span className="font-semibold text-gray-900">{item.q}</span>
        </span>
        <ChevronDown
          className={`h-5 w-5 text-primary-600 flex-shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {/* La respuesta se renderiza siempre y se oculta con CSS. Si se
          desmontara, el texto no existiría en el DOM y los buscadores no
          podrían indexarlo. */}
      <div
        id={panelId}
        className={`px-5 pb-5 pl-14 text-gray-600 whitespace-pre-line ${isOpen ? '' : 'hidden'}`}
      >
        {item.a}
      </div>
    </div>
  );
}

function LandingPatients() {
  const [openIndex, setOpenIndex] = useState(0);

  useSeoMeta(
    'Consulta médica online en Colombia | DocEnLinea',
    'Agenda una consulta médica online por videollamada o chat con médicos generales colombianos verificados. Precio claro, fórmula digital y atención rápida desde tu casa.',
    '/pacientes'
  );
  const { pricing } = usePricing();
  const questions = useMemo(() => buildQuestions(pricing), [pricing]);
  useFaqJsonLd(questions);
  // Con el registro de médicos cerrado (panel admin) se ocultan los enlaces a /medicos.
  const { open: doctorRegistrationOpen } = useDoctorRegistrationOpen();

  // Primer paso del embudo: es la landing a la que apuntan los anuncios.
  useEffect(() => {
    trackFunnelStep(FUNNEL.VIEW_LANDING);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Navbar (idéntico a HomePage) */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center">
              <Stethoscope className="h-8 w-8 text-primary-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">DocEnLinea</span>
            </Link>
            <div className="hidden md:flex items-center gap-6 mr-4">
              <Link to="/pacientes" className="text-primary-600 text-sm font-semibold">Para pacientes</Link>
              {doctorRegistrationOpen && (
                <Link to="/medicos" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Para médicos</Link>
              )}
              <Link to="/faq" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Preguntas frecuentes</Link>
              <Link to="/soporte" className="text-gray-600 hover:text-primary-600 text-sm font-medium">Soporte</Link>
            </div>
            <div className="flex gap-4">
              <Link to="/login" className="btn-secondary">Iniciar Sesión</Link>
              <Link to="/register" className="btn-primary">Registrarse</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <ShieldCheck className="h-4 w-4" /> Médicos colombianos verificados
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Consulta médica online para pacientes
            <br />
            <span className="text-primary-600">rápida, clara y sin salir de casa</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Resuelve tus dudas de salud por videollamada o chat con un médico general colombiano.
            {pricing
              ? `Consulta desde ${formatCOP(pricing.basePrice)}, fórmula digital y confirmación por WhatsApp.`
              : 'Precio claro, fórmula digital y confirmación por WhatsApp.'}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/register" className="btn-primary text-lg px-8 py-3">
              Registrarme y agendar mi cita
            </Link>
            <a href="#preguntas" className="btn-secondary text-lg px-8 py-3">
              Ver preguntas frecuentes
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Preguntas frecuentes clave (SEO) */}
        <section id="preguntas" className="scroll-mt-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">
            Todo lo que un paciente necesita saber
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Respondemos las preguntas más importantes antes de tu primera consulta con DocEnLinea.
          </p>
          <div className="space-y-3">
            {questions.map((item, i) => (
              <FaqRow
                key={item.q}
                item={item}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </section>

        {/* CTA intermedio */}
        <section className="mt-20 bg-primary-600 rounded-2xl p-10 sm:p-12 text-white text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Agenda tu consulta en menos de 5 minutos
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Regístrate, elige el horario que mejor te quede y realiza el pago seguro. Tu
            médico te espera por videollamada o chat.
          </p>
          <Link
            to="/register"
            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors inline-block"
          >
            Crear mi cuenta de paciente
          </Link>
        </section>

        {/* Enlace cruzado a landing de médicos */}
        {doctorRegistrationOpen && (
        <section className="mt-16 text-center">
          <p className="text-gray-600">
            ¿Eres profesional de la salud?{' '}
            <Link to="/medicos" className="text-primary-600 font-medium hover:underline">
              Conoce cómo unirte a DocEnLinea como médico
            </Link>
          </p>
        </section>
        )}
      </main>

      {/* Footer (idéntico al resto del sitio) */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <Stethoscope className="h-8 w-8" />
            <span className="ml-2 text-xl font-bold">DocEnLinea</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <Link to="/pacientes" className="hover:text-white">Para pacientes</Link>
            {doctorRegistrationOpen && <Link to="/medicos" className="hover:text-white">Para médicos</Link>}
            <Link to="/faq" className="hover:text-white">Preguntas Frecuentes</Link>
            <Link to="/soporte" className="hover:text-white">Soporte</Link>
            <Link to="/login" className="hover:text-white">Iniciar Sesión</Link>
            <Link to="/register" className="hover:text-white">Registrarse</Link>
          </div>
          <nav aria-label="Guías de salud" className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-4">
            <span className="text-gray-500">Guías de salud:</span>
            {HEALTH_GUIDES.map(({ href, label }) => (
              <a key={href} href={href} className="hover:text-white">{label}</a>
            ))}
          </nav>
          <p className="text-gray-500 text-xs mt-4 max-w-xl mx-auto">
            DocEnLinea NO es un servicio de emergencias. Ante una urgencia vital, llama al 123
            o acude al servicio de urgencias más cercano.
          </p>
          <p className="text-gray-500 text-sm mt-4">
            © 2025 DocEnLinea. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPatients;
